import { useCallback, useRef } from 'react';
import { useScheduleStore } from '../stores/scheduleStore';
import {
  fetchScheduleFromMonday,
  updateItemTimeline,
  updateItemAssignee,
  type UpdateSchedulePayload,
  type BulkMovePayload,
} from '../api/schedule.api';
import { getFieldMappingLocal, getSubitemFieldMappingLocal, getItemLevelLocal, getColorSchemaLocal, getPerPersonDistributionLocal, getBoardConfigsBulk, isBoardConfigured, EMPTY_FIELD_MAPPING } from '../api/board-config.api';
import { useWorkloadStore } from '../stores/workloadStore';
import { buildReassignedEntries } from '../utils/assignee-helpers';
import { addDays, parseISO, format } from 'date-fns';

/**
 * Resolve per-person distribution for a board.
 * Uses the board-level cached value; falls back to the resolved account default
 * stored in useWorkloadStore (which SchedulerBoard resolves at load time).
 */
function resolvePerPersonDistribution(boardId: number): boolean {
  const boardValue = getPerPersonDistributionLocal(boardId);
  if (boardValue !== null) return boardValue;
  // null means "use account default" — the workload store holds the resolved value
  return useWorkloadStore.getState().perPersonDistribution;
}

/**
 * Resolve the correct field mapping and board ID for a schedule item.
 * Subitems use the subitem field mapping and the subitems board ID.
 */
function resolveItemMapping(item: { boardId?: number; isSubitem?: boolean; subitemsBoardId?: number }, parentBoardId: number) {
  if (item.isSubitem) {
    // Subitems require their own board ID for API write-back.
    // If subitemsBoardId is missing (0 or undefined), we cannot write back correctly.
    if (!item.subitemsBoardId) {
      return { mapping: null, writeBoardId: 0 };
    }
    const subMapping = getSubitemFieldMappingLocal(item.boardId ?? parentBoardId);
    return { mapping: subMapping, writeBoardId: item.subitemsBoardId };
  }
  const mapping = getFieldMappingLocal(item.boardId ?? parentBoardId);
  return { mapping, writeBoardId: item.boardId ?? parentBoardId };
}

/**
 * Hook combining the schedule Zustand store with monday.com API calls.
 *
 * All planning data is fetched directly from monday.com boards
 * using the field mapping configuration. No backend sync required.
 */
export function useSchedule() {
  // Access actions via getState() to get stable references that don't cause
  // re-renders. This prevents fetch loops where setLoading → re-render →
  // new fetchSchedule ref → effect re-runs → setLoading → ...
  const store = useScheduleStore.getState;

  // Guard against stale responses when rapid date navigation fires overlapping fetches.
  // Both fetchSchedule and fetchMultiBoardSchedule share this counter because they
  // write to the same store — a call to one invalidates any pending call to the other.
  const fetchIdRef = useRef(0);

  const fetchSchedule = useCallback(
    async (boardId: number, startDate: string, endDate: string, options?: { silent?: boolean }) => {
      const currentFetchId = ++fetchIdRef.current;
      const { setLoading, setError, setItems, setBrokenBoardIds } = store();
      if (!options?.silent) {
        setLoading(true);
      }
      setError(null);
      setBrokenBoardIds([]);
      try {
        const fieldMapping = getFieldMappingLocal(boardId);
        const itemLevel = getItemLevelLocal(boardId);
        const subitemFieldMapping = getSubitemFieldMappingLocal(boardId);
        if (!isBoardConfigured({ fieldMapping, subitemFieldMapping, itemLevel })) {
          if (fetchIdRef.current !== currentFetchId) return;
          setItems([]);
          return;
        }

        const colorSchema = getColorSchemaLocal(boardId);

        // Drive monday's timeline filter from whichever mapping the board uses.
        // Sub-only boards leave the main timeline column null, so fall back to
        // the subitem timeline column to keep the query bounded.
        const filterTimelineColumnId =
          fieldMapping?.timelineColumnId ?? subitemFieldMapping?.timelineColumnId ?? null;
        const filter = filterTimelineColumnId
          ? { timeline: { columnId: filterTimelineColumnId, startDate, endDate } }
          : {};
        const perPersonDist = resolvePerPersonDistribution(boardId);
        let hadPartial = false;
        const items = await fetchScheduleFromMonday(
          boardId,
          fieldMapping ?? EMPTY_FIELD_MAPPING,
          filter,
          itemLevel,
          subitemFieldMapping,
          colorSchema,
          perPersonDist,
          () => { hadPartial = true; },
        );
        if (fetchIdRef.current !== currentFetchId) return; // stale — discard
        setItems(items);
        if (hadPartial) store().setBrokenBoardIds([boardId]);
      } catch (err) {
        if (fetchIdRef.current !== currentFetchId) return;
        const message = err instanceof Error ? err.message : 'Failed to load schedule';
        store().setError(message);
        // Track this board as broken so the header chip can flag it.
        store().setBrokenBoardIds([boardId]);
      } finally {
        if (fetchIdRef.current !== currentFetchId) return;
        store().setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [store],
  );

  const update = useCallback(
    async (id: string, payload: UpdateSchedulePayload, boardId?: number) => {
      const { items, updateItem, removeItem, setError } = store();
      const previousItem = items.find((item) => item.id === id);

      updateItem(id, payload);

      if (!previousItem || !boardId) return;

      const { mapping: fieldMapping, writeBoardId } = resolveItemMapping(previousItem, boardId);
      if (!fieldMapping) return;

      try {
        if (payload.startDate || payload.endDate) {
          const newStart = payload.startDate ?? previousItem.startDate;
          const newEnd = payload.endDate ?? previousItem.endDate;
          if (fieldMapping.timelineColumnId) {
            await updateItemTimeline(
              writeBoardId,
              previousItem.mondayItemId,
              fieldMapping.timelineColumnId,
              newStart,
              newEnd,
            );
          }
        }

        if (payload.assigneeId !== undefined && fieldMapping.assigneesColumnId) {
          await updateItemAssignee(
            writeBoardId,
            previousItem.mondayItemId,
            fieldMapping.assigneesColumnId,
            [payload.assigneeId],
          );
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to update item';
        setError(message);

        if (previousItem) {
          store().updateItem(id, previousItem);
        } else {
          removeItem(id);
        }
      }
    },
    [store],
  );

  const remove = useCallback(
    async (id: string, boardId?: number) => {
      const { items, removeItem, addItem, setError } = store();
      const item = items.find((i) => i.id === id);
      if (!item) return;

      removeItem(id);

      if (boardId) {
        const { mapping: fieldMapping, writeBoardId } = resolveItemMapping(item, boardId);
        if (fieldMapping?.timelineColumnId) {
          try {
            await updateItemTimeline(
              writeBoardId,
              item.mondayItemId,
              fieldMapping.timelineColumnId,
              '',
              '',
            );
          } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to remove item';
            setError(message);
            addItem(item);
          }
        }
      }
    },
    [store],
  );

  const bulkMoveItems = useCallback(
    async (payload: BulkMovePayload, boardId?: number) => {
      const { items: allItems, updateItem } = store();

      for (const itemId of payload.itemIds) {
        const item = allItems.find((i) => i.id === itemId);
        if (!item) continue;

        const newStart = format(addDays(parseISO(item.startDate), payload.daysDelta), 'yyyy-MM-dd');
        const newEnd = format(addDays(parseISO(item.endDate), payload.daysDelta), 'yyyy-MM-dd');

        updateItem(itemId, { startDate: newStart, endDate: newEnd });

        if (boardId) {
          const { mapping: fieldMapping, writeBoardId } = resolveItemMapping(item, boardId);
          if (fieldMapping?.timelineColumnId) {
            try {
              await updateItemTimeline(
                writeBoardId,
                item.mondayItemId,
                fieldMapping.timelineColumnId,
                newStart,
                newEnd,
              );
            } catch {
              store().updateItem(itemId, { startDate: item.startDate, endDate: item.endDate });
            }
          }
        }
      }
    },
    [store],
  );

  const bulkUnplanItems = useCallback(
    async (itemIds: string[], boardId?: number) => {
      const { items: allItems, removeItem, addItem } = store();

      for (const itemId of itemIds) {
        const item = allItems.find((i) => i.id === itemId);
        if (!item) continue;

        removeItem(itemId);

        if (boardId) {
          const { mapping: fieldMapping, writeBoardId } = resolveItemMapping(item, boardId);
          if (fieldMapping?.timelineColumnId) {
            try {
              await updateItemTimeline(
                writeBoardId,
                item.mondayItemId,
                fieldMapping.timelineColumnId,
                '',
                '',
              );
            } catch {
              addItem(item);
            }
          }
        }
      }
    },
    [store],
  );

  const bulkReassignItems = useCallback(
    async (itemIds: string[], targetUserId: number, boardId?: number) => {
      const { items: allItems, updateItem } = store();

      for (const itemId of itemIds) {
        const item = allItems.find((i) => i.id === itemId);
        if (!item) continue;

        const previousAssigneeId = item.assigneeId;
        updateItem(itemId, { assigneeId: targetUserId });

        if (boardId) {
          const { mapping: fieldMapping, writeBoardId } = resolveItemMapping(item, boardId);
          if (fieldMapping?.assigneesColumnId) {
            try {
              // Preserve other assignees on multi-assignee items
              const entries = buildReassignedEntries(
                allItems, item.mondayItemId,
                previousAssigneeId, item.assignedTeamId,
                targetUserId, undefined, 'person',
              );
              await updateItemAssignee(
                writeBoardId,
                item.mondayItemId,
                fieldMapping.assigneesColumnId,
                entries,
              );
            } catch {
              store().updateItem(itemId, { assigneeId: previousAssigneeId });
            }
          }
        }
      }
    },
    [store],
  );

  const fetchMultiBoardSchedule = useCallback(
    async (boardIds: number[], startDate: string, endDate: string, options?: { silent?: boolean }) => {
      const currentFetchId = ++fetchIdRef.current;
      const { setLoading, setError, setItems, setBrokenBoardIds } = store();
      if (!options?.silent) {
        setLoading(true);
      }
      setError(null);
      setBrokenBoardIds([]);
      const brokenIds: number[] = [];
      try {
        // Warm per-board caches (field mapping, item level, color schema, etc.)
        // so that getFieldMappingLocal() returns data for all boards, not just
        // those previously loaded via getBoardConfig().
        await getBoardConfigsBulk(boardIds);

        // Fetch boards with bounded parallelism. Per-request complexity is
        // small now that we pass `column_values(ids: [...])` and a page limit
        // of 100, so we can safely run more requests in parallel without
        // blowing monday.com's shared complexity budget (5M points/min reads).
        const MAX_CONCURRENT = 8;
        const allItems: Awaited<ReturnType<typeof fetchScheduleFromMonday>> = [];
        for (let i = 0; i < boardIds.length; i += MAX_CONCURRENT) {
          if (fetchIdRef.current !== currentFetchId) return; // stale — abort early
          const batch = boardIds.slice(i, i + MAX_CONCURRENT);
          // Use allSettled so a single misconfigured board (e.g. timeline column
          // was deleted on monday) doesn't take down the whole batch. Failed
          // boards are logged and skipped; others load normally.
          const batchResults = await Promise.allSettled(
            batch.map((boardId) => {
              const fieldMapping = getFieldMappingLocal(boardId);
              if (!fieldMapping) return Promise.resolve([]);
              const itemLevel = getItemLevelLocal(boardId);
              const subitemFieldMapping = getSubitemFieldMappingLocal(boardId);
              const colorSchema = getColorSchemaLocal(boardId);
              const filter = fieldMapping.timelineColumnId
                ? { timeline: { columnId: fieldMapping.timelineColumnId, startDate, endDate } }
                : {};
              const perPersonDist = resolvePerPersonDistribution(boardId);
              return fetchScheduleFromMonday(
                boardId,
                fieldMapping,
                filter,
                itemLevel,
                subitemFieldMapping,
                colorSchema,
                perPersonDist,
                () => { if (!brokenIds.includes(boardId)) brokenIds.push(boardId); },
              );
            }),
          );
          for (let b = 0; b < batchResults.length; b++) {
            const r = batchResults[b];
            if (r.status === 'fulfilled') {
              allItems.push(...r.value);
            } else {
              if (!brokenIds.includes(batch[b])) brokenIds.push(batch[b]);
              console.warn('[schedule] Board failed to load, skipping', { boardId: batch[b], reason: r.reason });
            }
          }
        }
        if (fetchIdRef.current !== currentFetchId) return; // stale — discard
        setItems(allItems);
        if (brokenIds.length > 0) setBrokenBoardIds(brokenIds);
      } catch (err) {
        if (fetchIdRef.current !== currentFetchId) return;
        const message = err instanceof Error ? err.message : 'Failed to load multi-board schedule';
        store().setError(message);
      } finally {
        if (fetchIdRef.current !== currentFetchId) return;
        store().setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [store],
  );

  return {
    fetchSchedule,
    fetchMultiBoardSchedule,
    update,
    remove,
    bulkMoveItems,
    bulkUnplanItems,
    bulkReassignItems,
  };
}
